import { useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '../../../Modal';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import {
  feedbackCountsLine,
  feedbackDesignUrl,
  feedbackReactionGlyph,
  type FeedbackReplyCardData,
  type FeedbackRoundView,
} from '../../../../lib/feedback-reply-card';
import type { FeedbackReaction } from '../../../../lib/feedback-page';
import { useActiveConversation } from '../../../chat/ActiveConversationContext';
import { API_BASE } from '../../../../lib/api-base';
import { apiFetch } from '../../../../lib/api-client';
import { Button, Card, Typography } from '../../ui';
import { AssistantMarkdownContent } from '../MessageRow/AssistantMarkdownContent';
import styles from './FeedbackReplyCard.module.css';

/**
 * One answer to the owner's feedback round, drawn in place of a bubble for the row the outreach service writes
 * (`metadata.feedbackReplyCard`, or an older raw row read off its line — `lib/feedback-reply-card.ts`).
 *
 * The design and its title, who answered, the reaction as its glyph and label, the comment in quotes, and the round's
 * count; "See all answers" opens the round (its answers, and the summary with "Apply these comments" once written),
 * "Open design" opens Wixel's link to it. The name and the comment are someone else's words: rendered as TEXT only —
 * React escapes them, and nothing here passes them to a markdown or HTML renderer.
 *
 * Mirrors iOS `FeedbackReplyCardView` and Android `FeedbackReplyCard`.
 */
export function FeedbackReplyCard({
  card,
  conversationId,
  canSend = true,
}: {
  card: FeedbackReplyCardData;
  conversationId?: string;
  canSend?: boolean;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [open, setOpen] = useState(false);
  const counts = feedbackCountsLine(card);
  const designUrl = feedbackDesignUrl(card);
  const title = card.assetTitle ? t('feedbackReply.title', { title: card.assetTitle }) : t('feedbackReply.titleUntitled');

  return (
    <Card variant="outline" size="sm" className={styles.card} data-testid="feedback-reply-card">
      <div className={styles.header}>
        {card.assetPreviewUrl ? (
          <img className={styles.thumb} src={card.assetPreviewUrl} alt="" loading="lazy" />
        ) : (
          <div className={`${styles.thumb} ${styles.thumbEmpty}`} aria-hidden>
            {card.reaction ? feedbackReactionGlyph(card.reaction) : '💬'}
          </div>
        )}
        <div className={styles.headText}>
          <Typography variant="small-medium" component="h3" className={styles.title}>
            {title}
          </Typography>
          {counts && (
            <Typography variant="p3" color="secondary" component="p" data-testid="feedback-reply-counts">
              {t(counts.key, counts.values)}
            </Typography>
          )}
        </div>
      </div>
      <FeedbackAnswer who={card.answererName} reaction={card.reaction} comment={card.comment} />
      <div className={styles.actions}>
        <Button
          size="small"
          variant="outlined"
          data-testid="feedback-reply-see-all"
          onClick={() => {
            logPageClickBi({ page_name: 'chat', button_name: 'feedback_reply_see_all', conversation_id: conversationId });
            setOpen(true);
          }}
        >
          {t('feedbackReply.seeAll')}
        </Button>
        {designUrl && (
          <Button
            size="small"
            variant="text"
            href={designUrl}
            external
            target="_blank"
            rel="noopener noreferrer"
            data-testid="feedback-reply-open-design"
            onClick={() => logPageClickBi({ page_name: 'chat', button_name: 'feedback_reply_open_design', conversation_id: conversationId })}
          >
            {t('feedbackReply.openDesign')}
          </Button>
        )}
      </div>
      {open && <FeedbackRoundSheet roundId={card.roundId} conversationId={conversationId} canSend={canSend} onClose={() => setOpen(false)} />}
    </Card>
  );
}

/** One answer: who, the reaction chip, the comment in quotes. Text only. */
function FeedbackAnswer({ who, reaction, comment }: { who?: string; reaction?: FeedbackReaction; comment?: string }) {
  const { t } = useTranslation();
  return (
    <div className={styles.answer}>
      <Typography variant="small-medium" component="span">
        {who ?? t('feedbackReply.someone')}
      </Typography>
      {reaction && (
        <Typography variant="p2" component="span" data-testid="feedback-reply-reaction">{`${feedbackReactionGlyph(reaction)} ${t(
          `feedbackPage.reaction.${reaction}`,
        )}`}</Typography>
      )}
      {comment && (
        <Typography variant="p2" component="p" className={styles.comment} data-testid="feedback-reply-comment">
          {`“${comment}”`}
        </Typography>
      )}
    </div>
  );
}

async function fetchFeedbackRound(roundId: string): Promise<FeedbackRoundView> {
  const res = await apiFetch(`${API_BASE}/api/feedback-rounds/${encodeURIComponent(roundId)}`);
  if (!res.ok) throw new Error(`feedback round ${res.status}`);
  return (await res.json()) as FeedbackRoundView;
}

/** "See all answers": the round's answers, and its summary with "Apply these comments" once the agent wrote it. */
export function FeedbackRoundSheet({
  roundId,
  conversationId,
  canSend,
  onClose,
}: {
  roundId: string;
  conversationId?: string;
  canSend: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const { openChatWithMessage } = useActiveConversation();
  const { logPageClickBi } = useGenericBi();
  const round = useQuery({ queryKey: ['feedback-round', roundId], queryFn: () => fetchFeedbackRound(roundId), staleTime: 15_000 });
  const view = round.data;
  const designUrl = view ? feedbackDesignUrl({ assetPreviewUrl: view.assetPreviewUrl, assetMediaUrl: view.assetMediaUrl }) : undefined;
  const targetConversation = view?.conversationId ?? conversationId;
  const apply = () => {
    if (!view || !targetConversation) return;
    logPageClickBi({ page_name: 'chat', button_name: 'feedback_reply_apply', conversation_id: targetConversation });
    const message = view.assetTitle ? t('feedbackReply.applyMessage', { title: view.assetTitle }) : t('feedbackReply.applyMessageUntitled');
    openChatWithMessage({ kind: 'existing', conversationId: targetConversation, message });
    onClose();
  };
  const counts = view ? feedbackCountsLine({ counts: { answered: view.answered, asked: view.asked, answers: view.replies.length } }) : null;

  return (
    <Modal open onClose={onClose} presentation="sheet" title={t('feedbackReply.sheetTitle')} labelledBy={titleId} maxHeightPx={650} scrollBody>
      <div className={styles.sheetBody} data-testid="feedback-round-sheet">
        {round.isLoading && (
          <Typography variant="p2" color="secondary" component="p">
            {t('feedbackReply.loading')}
          </Typography>
        )}
        {round.isError && (
          <Typography variant="p2" color="secondary" component="p">
            {t('feedbackReply.loadFailed')}
          </Typography>
        )}
        {view && (
          <>
            <div className={styles.header}>
              {view.assetPreviewUrl && <img className={styles.thumb} src={view.assetPreviewUrl} alt="" />}
              <div className={styles.headText}>
                <Typography variant="small-medium" component="p" className={styles.title}>
                  {view.assetTitle ?? t('feedbackReply.titleUntitled')}
                </Typography>
                {counts && (
                  <Typography variant="p3" color="secondary" component="p">
                    {t(counts.key, counts.values)}
                  </Typography>
                )}
              </div>
            </div>
            {view.replies.length === 0 ? (
              <Typography variant="p2" color="secondary" component="p">
                {t('feedbackReply.noAnswers')}
              </Typography>
            ) : (
              <ul className={styles.replies}>
                {view.replies.map((reply, i) => (
                  <li key={`${reply.at ?? ''}-${i}`} className={styles.reply}>
                    <FeedbackAnswer who={reply.from} reaction={reply.reaction} comment={reply.text} />
                  </li>
                ))}
              </ul>
            )}
            {view.summary ? (
              <Card variant="sunken" size="sm" data-testid="feedback-round-summary">
                <Typography variant="small-medium" component="h4">
                  {t('feedbackReply.summary')}
                </Typography>
                <AssistantMarkdownContent displayed={view.summary} isCursorOn={false} />
              </Card>
            ) : (
              <Typography variant="p3" color="secondary" component="p">
                {t('feedbackReply.summaryPending')}
              </Typography>
            )}
            <div className={styles.actions}>
              {view.summary && targetConversation && (
                <Button size="small" color="accent" onClick={apply} disabled={!canSend} data-testid="feedback-round-apply">
                  {t('feedbackReply.apply')}
                </Button>
              )}
              {designUrl && (
                <Button size="small" variant="outlined" href={designUrl} external target="_blank" rel="noopener noreferrer">
                  {t('feedbackReply.openDesign')}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
